import { globalVariables } from "helper/globalVariables";
import MultiSelectDropdown from "shared/Components/multiselectDropdown";
import { useEffect, useRef, useState, useContext } from "react";
import { TableWithPagination } from "shared/Components/tableWithPagination";
import { globalClasses } from "helper/globalClasses";
import { UserListMobileTable } from "./components/UserListMobileTable";
import SearchBar from "shared/Components/search";
import { useLocation, useNavigate } from "react-router-dom";
import ThreeDotMenu from "shared/Components/threeDotMenu";
import { useMediaQuery } from "react-responsive";
import { DEFAULT_DATE_FORMAT, ROLE } from "helper/utility";
import { useLazyQuery } from "@apollo/client";
import {
  DeleteUserByAuthId,
  getAllRegisteredMemberQuery,
  getAllMembersForExportQuery,
  updateUserRolesMutation,
} from "redux/features/superAdminSlice/manageUsersSlice/manageUsersGql";
import moment from "moment";
import { debounce } from "lodash";
import { client } from "helper/apolloConfig";
import { PopUpMessages } from "shared/Components/popUpMessages";
import Loader from "shared/Components/loader";
import { RoleContext } from "context/RoleContext";
import PageInfo from "shared/Components/pageInfo";
import Modal from "shared/Components/modal";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

const threeDotData = [globalVariables.edit, globalVariables.delete];

const headData = [
  globalVariables.name,
  globalVariables.email,
  globalVariables.phoneNumber,
  globalVariables.memberSince,
  globalVariables.city,
  globalVariables.state,
  // globalVariables.address,
  globalVariables.role,
];

export const ManageUsers = ({ editable = true }) => {
  const modalRef = useRef();
  const { paginatedDataCls } = globalClasses;
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [roleLoading, setRoleLoading] = useState(false);
  const isMobile = useMediaQuery({ query: "(max-width: 480px)" });
  const navigate = useNavigate();
  const [getData, { loading, error, data, refetch }] = useLazyQuery(
    getAllRegisteredMemberQuery
  );
  const resultData = data?.getAllRegisteredMember;
  const usersData = resultData?.data || [];
  const role = useContext(RoleContext);
  const [apiData, setApiData] = useState({
    title: "",
    desc: "Role Updated Successfully.",
    type: "pending",
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const downloadModalRef = useRef();
  const [downloadLoading, setDownloadLoading] = useState(false);

  const EXCLUDED_EMAIL = "oadillabough@gmail.com";

  const handleOpenDownloadModal = () => {
    downloadModalRef.current?.openModal();
  };

  const fetchAllMembersForExport = async () => {
    try {
      const result = await client.query({
        query: getAllMembersForExportQuery,
        variables: {
          pageNumber: 1,
          pageSize: 10000,
          isBlocked: false,
        },
        fetchPolicy: "network-only",
      });
      const members = result?.data?.getAllRegisteredMember?.data || [];
      return members.filter(
        (m) => m.email?.toLowerCase() !== EXCLUDED_EMAIL.toLowerCase()
      );
    } catch (err) {
      console.error("Failed to fetch members for export:", err);
      return [];
    }
  };

  const handleDownloadCSV = async () => {
    setDownloadLoading(true);
    try {
      const members = await fetchAllMembersForExport();
      const sorted = [...members]
        .filter((m) => m.name && m.email)
        .sort((a, b) => (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase()));

      const csvRows = ["Name,Email"];
      sorted.forEach((m) => {
        const name = (m.name || "").replace(/"/g, '""');
        const email = (m.email || "").replace(/"/g, '""');
        csvRows.push(`"${name}","${email}"`);
      });

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, "KSG_Member_Emails.csv");
      downloadModalRef.current?.closeModal();
    } catch (err) {
      console.error("CSV download error:", err);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloadLoading(true);
    try {
      const members = await fetchAllMembersForExport();

      const getRolePriority = (member) => {
        const roles = member.role || [];
        if (roles.includes("Super Admin") || roles.includes("Superadmin"))
          return 0;
        if (roles.includes("Host")) return 1;
        return 2;
      };

      const getRoleLabel = (member) => {
        const roles = member.role || [];
        if (roles.includes("Super Admin") || roles.includes("Superadmin"))
          return "Super Admin";
        if (roles.includes("Host")) return "Host";
        return "Member";
      };

      const sortedMembers = [...members].sort((a, b) => {
        const priorityDiff = getRolePriority(a) - getRolePriority(b);
        if (priorityDiff !== 0) return priorityDiff;
        return (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase());
      });

      const wb = XLSX.utils.book_new();
      const wsData = [];

      let currentSection = null;
      sortedMembers.forEach((member) => {
        const section = getRoleLabel(member);
        if (section !== currentSection) {
          if (currentSection !== null) {
            wsData.push([]);
          }
          wsData.push([section + "s"]);
          wsData.push(["Name", "Email"]);
          currentSection = section;
        }
        wsData.push([member.name || "", member.email || ""]);
      });

      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Style column widths
      ws["!cols"] = [{ wch: 30 }, { wch: 40 }];

      // Bold section headers and column headers
      let rowIdx = 0;
      let currentSectionForStyle = null;
      sortedMembers.forEach((member) => {
        const section = getRoleLabel(member);
        if (section !== currentSectionForStyle) {
          if (currentSectionForStyle !== null) {
            rowIdx++; // blank row
          }
          // Section header row
          if (ws[XLSX.utils.encode_cell({ r: rowIdx, c: 0 })]) {
            ws[XLSX.utils.encode_cell({ r: rowIdx, c: 0 })].s = {
              font: { bold: true, sz: 14 },
            };
          }
          rowIdx++;
          // Column header row
          if (ws[XLSX.utils.encode_cell({ r: rowIdx, c: 0 })]) {
            ws[XLSX.utils.encode_cell({ r: rowIdx, c: 0 })].s = {
              font: { bold: true },
            };
          }
          if (ws[XLSX.utils.encode_cell({ r: rowIdx, c: 1 })]) {
            ws[XLSX.utils.encode_cell({ r: rowIdx, c: 1 })].s = {
              font: { bold: true },
            };
          }
          rowIdx++;
          currentSectionForStyle = section;
        } else {
          rowIdx++;
        }
      });

      XLSX.utils.book_append_sheet(wb, ws, "Members");
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, "KSG_Member_Emails.xlsx");
      downloadModalRef.current?.closeModal();
    } catch (err) {
      console.error("Excel download error:", err);
    } finally {
      setDownloadLoading(false);
    }
  };

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  let refresh = queryParams.get("page");

  const handlePaginationData = (page) => {
    setPage(page);
  };

  if (refresh === "refresh") {
    refresh = "";
    navigate("/admin/manage/users");
    window?.location?.reload();
  }

  const fetchData = ({ page, search }) => {
    getData({
      variables: {
        pageNumber: page,
        pageSize: 10,
        search,
        isBlocked: false,
      },
    });
  };
  useEffect(() => {
    fetchData({
      search,
      page,
    });
    // eslint-disable-next-line
  }, [search, page]);

  const onSearch = (event) => {
    const value = event?.target?.value;
    setPage(1);
    if (value) {
      setSearch(value);
    } else {
      setSearch("");
    }
  };

  const debouncedSearch = debounce(onSearch, 1000);

  const userRoles = [
    { id: "1", title: ROLE.Member, isSelected: false },
    { id: "2", title: ROLE.Host, isSelected: false },
    { id: "3", title: ROLE.Judge, isSelected: false },
  ];

  const onDeleteUser = async (authId) => {
    try {
      setDeleteLoading(true);
      const result = await client.mutate({
        mutation: DeleteUserByAuthId,
        variables: { authId: authId },
      });
      if (result?.data?.deactivateUser) {
        modalRef.current.openModal();
        setDeleteLoading(false);
        setApiData({
          ...apiData,
          title: globalVariables.success,
          desc: result?.data?.deactivateUser?.message,
          type: "success",
        });
      }
    } catch (error) {
      setDeleteLoading(false);
      modalRef.current.openModal();
      setApiData({
        ...apiData,
        title: globalVariables.error,
        desc: error?.message,
        type: "pending",
      });
      setRoleLoading(false);
    }
  };

  const onSelectOption = async ({ user, option }) => {
    if (option === "Edit") {
      if (user?.role?.includes("Judge")) {
        navigate(`/admin/manage/users/${user.authId}/Judge/update`);
      } else if (user?.role?.includes("Host")) {
        navigate(`/admin/manage/users/${user.authId}/Host/update`);
      } else {
        navigate(`/admin/manage/users/${user.authId}/Member/update`);
      }
    } else if (option === "Delete") {
      onDeleteUser(user?.authId);
    }
  };

  const handleUpdateRole = async ({ authId, roles }) => {
    setRoleLoading(true);

    const payload = {
      authId,
      role: roles,
    };
    try {
      const result = await client.mutate({
        mutation: updateUserRolesMutation,
        variables: { ...payload },
      });

      if (result?.data?.updateUserRoles) {
        modalRef.current.openModal();
        setApiData({
          ...apiData,
          title: "Role updated Successfully",
          desc: result?.data?.message,
          type: "success",
        });
      }
      setRoleLoading(false);
    } catch (error) {
      modalRef.current.openModal();
      setApiData({
        ...apiData,
        title: globalVariables.error,
        desc: error?.message,
        type: "pending",
      });
      setRoleLoading(false);
    }
  };

  
  const onGetDetail = (id) => {
    navigate(`/admin/manage/users/${id}`);
  };

  const onGetDetailMobile = (user) => {
    navigate(`/admin/manage/users/${user?.authId}`);
  };

  const renderTableBody = () => {
    return usersData.map((user, index) => (
      <tr
        className={`key={user.id} ${role === 'Super Admin' ? 'cursor-pointer' : 'cursor-default'}`}

        key={user.id}
        onClick={() => {
          if (editable) {
            onGetDetail(user?.authId);
          }
        }}
      >
        <td className={paginatedDataCls(index)}>{user.name}</td>
        <td className={paginatedDataCls(index, "w-[50px]")}>{user.email}</td>
        <td className={paginatedDataCls(index)}>
          {user.phone_number ? user.phone_number : "-"}
        </td>
        <td className={paginatedDataCls(index)}>
          {user.createdAt
            ? moment(user.createdAt).format("MMM YYYY")
            : "-"}
        </td>
        {/* <td className={paginatedDataCls(index)}>{user.location_address}</td> */}
        <td className={paginatedDataCls(index)}>
          {user.city && user?.city != "" ? user?.city : "-"}
        </td>
        <td className={paginatedDataCls(index)}>
          {user.state && user.state != "" ? user.state : "-"}
        </td>
        <td className={paginatedDataCls(index)}>
          {editable ? (
            <div className="flex">
              <div className="flex flex-1 justify-center">
                <MultiSelectDropdown
                  dropdownOptions={
                    user?.role?.length > 0
                      ? userRoles?.map((userRole) =>
                          user?.role?.includes(userRole?.title)
                            ? {
                                ...userRole,
                                isSelected: true,
                              }
                            : { ...userRole }
                        )
                      : userRoles?.map((userRole) => ({
                          ...userRole,
                          isSelected: userRole?.title === ROLE.Member,
                        }))
                  }
                  placeholder={user?.role?.length > 1
                    ? user.role.map(r => r[0]).join(", ")
                    : ROLE.Member
                  }
                  width={"w-[150px]"}
                  className="h-auto"
                  onChange={(updateOptions) => {
                    const isSuperAdmin = user?.role.includes("Super Admin");
                    const updateRoles = updateOptions
                      ?.filter((option) => option?.isSelected)
                      ?.map((options) => options?.title);
                    handleUpdateRole({
                      authId: user?.authId,
                      roles: isSuperAdmin ? [...updateRoles, "Super Admin"] : updateRoles,
                    });
                  }}
                />
              </div>

              {deleteLoading && index === selectedIndex ? (
                <div className=" h-10 w-10 justify-center  flex items-center ">
                  <Loader />
                </div>
              ) : (
                <div
                  onClick={() => setSelectedIndex(index)}
                  className=" h-10 w-10 justify-center  flex items-center "
                >
                  <ThreeDotMenu
                    fromUser={true}
                    options={threeDotData}
                    onSelectOption={(option) =>
                      onSelectOption({ option, user })
                    }
                    dynamicHeight={50}
                  />
                </div>
              )}
            </div>
          ) : (
            <span>{user?.role ? user?.role?.join(", ") : "-"}</span>
          )}
        </td>
      </tr>
    ));
  };

  const onCloseModal = () => {
    refetch();
  };

  return (
    <div className="App rounded-md min-h-screen lg:ml-[17rem] sm:pt-8 bg-white lg:p-10">
      <PageInfo title={editable ? globalVariables.manageUsers : globalVariables.memberDetails}/>
      <div className="flex flex-col gap-4 visible min-[480px]:hidden  ">
        <span className="text-xl font-semibold text-gray-900 ">
          {globalVariables.detailsOdUsers}
        </span>
        <div className="flex justify-between items-center gap-2">
          <button
            onClick={handleOpenDownloadModal}
            className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-md text-sm whitespace-nowrap transition-colors"
          >
            Download
          </button>
          <SearchBar
            width="w-full"
            onChange={debouncedSearch}
            placeholder={globalVariables.search}
          />
        </div>
      </div>
      <div className="flex flex-1 justify-center relative mt-2">
        {isMobile ? (
          <div className="flex flex-col flex-1 flex-grow">
            <UserListMobileTable
              users={usersData}
              currentPage={resultData?.pageNumber}
              totalPages={resultData?.totalPages}
              loading={loading || roleLoading}
              error={error}
              handlePageChange={(page) => handlePaginationData(page)}
              onClick={onGetDetailMobile}
              handleUpdateRole={handleUpdateRole}
              userRoles={userRoles}
              editable={editable}
            />
          </div>
        ) : (
          <TableWithPagination
            isHeaderComponentRow={true}
            headerComponent={
              <div className="flex justify-between items-center w-full px-4">
                <button
                  onClick={handleOpenDownloadModal}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-6 rounded-md text-sm whitespace-nowrap transition-colors"
                >
                  Download
                </button>
                <SearchBar
                  onChange={debouncedSearch}
                  placeholder={globalVariables.search}
                />
              </div>
            }
            headData={headData}
            tableBody={renderTableBody()}
            itemsPerPage={10}
            // overflow = "overflow-auto"
            tableHeight={"md:h-[calc(100vh-10rem)] lg:h-[100vh]"}
            totalPage={resultData?.totalPages}
            handlePageChange={(page) => handlePaginationData(page)}
            loading={loading || roleLoading}
            persistPagination={true}
            current={page}
            error={error}
          />
        )}
      </div>

      {/* Download Modal */}
      <Modal ref={downloadModalRef} extraClass="w-[400px]">
        <div className="flex flex-col items-center gap-6 py-4 px-2">
          <h2 className="text-xl font-bold text-gray-800">
            Download Member Emails
          </h2>
          <p className="text-sm text-gray-500 text-center">
            Select a format to download
          </p>
          <div className="flex flex-col gap-3 w-full">
            <button
              onClick={handleDownloadCSV}
              disabled={downloadLoading}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white font-medium py-3 px-6 rounded-md text-sm transition-colors"
            >
              {downloadLoading ? "Downloading..." : "Member Emails — CSV"}
            </button>
            <button
              onClick={handleDownloadExcel}
              disabled={downloadLoading}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white font-medium py-3 px-6 rounded-md text-sm transition-colors"
            >
              {downloadLoading ? "Downloading..." : "Member Emails — Excel"}
            </button>
          </div>
        </div>
      </Modal>

      <PopUpMessages
        title={globalVariables.success}
        desc={apiData.desc ? apiData.desc : apiData.title}
        type={apiData.type}
        ref={modalRef}
        onCloseModal={onCloseModal}
      />
    </div>
  );
};
